import { Action, createAction, createActionGroup, createFeature, createFeatureSelector, createReducer, emptyProps, on, Store } from '@ngrx/store';
import { EventInvitation } from '../../../globals/store/events.data';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import {  ofType, Actions, createEffect } from '@ngrx/effects';
import { of, EMPTY, Observable } from 'rxjs';
import { map, catchError, withLatestFrom, flatMap, mergeMap } from 'rxjs/operators';
import { LogService } from '../services/log.service';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { HttpClient } from '@angular/common/http';
import { capitalize } from '../helpers/helpers';
import { PermissionAccess } from '../../../globals/store/permissions.data';
import { WFWorker, WorkerProfile, WorkerWithProfile, WorkforceGroup } from '../../../globals/store/workforce.data';
import { permissionsActions } from './permissions.era';
import { eventsActions } from './events.era';
import { userActions } from './user.era';
import { jsonCopyObject, makeMapFromListOfObjectsAndKey } from 'globals/helpers/helpers';

export enum newWorkerStages {
  init = 'init',
  send_invite = 'send_invite',
  sending = 'sending',
  email_address = 'email_address',
  invite_existing = 'invite_existing'
}
export type NewWorkerStatus =  keyof typeof newWorkerStages

export interface WorkforceState {
  wf_groups: WorkforceGroup[]
  workforceGroupsById: { [id:string]: WorkforceGroup }
  allWorkforceGroupsById: { [id:string]: WorkforceGroup } // workforce group ids should be unique (except for really old groups that are named the same), so no real need to do by eventId
	workforce: WorkerWithProfile[]
  workerProfiles: EntityState<WorkerProfile>
  workerEmailList: string[]
  allWorkersByEmail: { [eventId: string]: { [key:string]: WorkerWithProfile } }
  allWorkersByUId: { [eventId: string]: { [key:string]: WorkerWithProfile } }
  workersByEmail: { [key:string]: WorkerWithProfile }
  workersByUId: { [key:string]: WorkerWithProfile }
  unloadedUserProfileUIds: Set<string>
  declined_workers: EventInvitation[]
  newWorkerStatus: NewWorkerStatus | undefined
  newWorker:WFWorker | undefined
  newWorkerPermissions:PermissionAccess | undefined
  loadingProfiles: boolean
}

const makeInitialState = (): WorkforceState => ({
  wf_groups: [],
  allWorkforceGroupsById: {},
  workforceGroupsById: {},
  workforce: [],
  declined_workers: [],
  unloadedUserProfileUIds: new Set(),
	workerProfiles: {
		ids: [],
		entities: {}
  },
  allWorkersByEmail: {},
  allWorkersByUId: {},
  workerEmailList: [],
  workersByEmail: {},
  workersByUId: {},
  loadingProfiles: false,
  newWorkerStatus: undefined,
  newWorker: undefined,
  newWorkerPermissions: undefined,
})

export const workerProfileAdapter = createEntityAdapter<WorkerProfile>({ selectId:(e:WorkerProfile)=>e.uid })

export const WorkforceActions = createActionGroup({
  source: 'Workforce',
  events: {
    'Load Downloaded Worker Profiles': (payload: WorkerProfile[]) => ({ payload }),
    'Save Role': (payload: WFWorker[]) => ({ payload }),
    'Change New Worker Status': (payload: NewWorkerStatus) => ({ payload }),
    'Get Fresh Worker': emptyProps(),
    'Update New Worker': (payload: Partial<WFWorker>) => ({ payload }),
    'Update New Worker Permissions': (payload: PermissionAccess) => ({ payload }),
    'Load New Worker And Permissions': (payload: { newWorker: WFWorker, newWorkerPermissions: PermissionAccess }) => ({ payload }),
  }
})


// Effects
@Injectable()
export class WorkforceEffects {

  private actions$ = inject(Actions);

  getWorkerProfiles: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(eventsActions.loadEvent),
    withLatestFrom(this.store.select(workforceFeature.selectUnloadedUserProfileUIds)),
    mergeMap(([action, unloadedUserProfileUIds]: [any, Set<string>]) => {
      if (!unloadedUserProfileUIds.size) {
        return EMPTY
      }
      // either also call /get-restrictions or incorporate getRestrictions(eventId) into /load-worker-profiles
      return this.http.post(`${this.api}/load-worker-profiles`, { workers: Array.from(unloadedUserProfileUIds) }).pipe(
        map(workerProfiles => {
          return WorkforceActions.loadDownloadedWorkerProfiles(<WorkerProfile[]>workerProfiles)
      }),
				catchError(err => {
					console.log("Error while trying to download worker profiles: ", err)
					return of(WorkforceActions.loadDownloadedWorkerProfiles([])) // <== Ensures getting rid of the "loading" status.  Perhaps it would be good to implement retries here
				})
      )
    })
  ));

  private api = `${environment.functionsURL}api/app`;
  constructor( public store:Store, public log:LogService, public http:HttpClient) {}
}


const extractWorkforceObjectsAndInitialize = (workforce, workerProfileEntities, wf_groups: WorkforceGroup[]) => {
  workforce = jsonCopyObject(workforce)
  // console.log({ workforce: jsonCopyObject(workforce), workerProfileEntities: jsonCopyObject(workerProfileEntities) })
  workforce.forEach((worker, index) => {
    if (workerProfileEntities[worker.uid]) workforce[index] = {...worker, ...workerProfileEntities[worker.uid]}
  }) // changes from worker profiles can / likely will be more recent than the archived workforce on the event
  // console.log({ workerProfileEntities: jsonCopyObject(workerProfileEntities)})
  const workersByEmail = makeMapFromListOfObjectsAndKey<WorkerWithProfile>(workforce, 'email')
  const workerEmailList = Object.keys(workersByEmail)
  const workersByUId = makeMapFromListOfObjectsAndKey<WorkerWithProfile>(workforce, 'uid')
  const unloadedUserProfileUIds: Set<string> = new Set(workforce.filter(w => w.status === 'accepted'
  && !workerProfileEntities[w.uid]).map(w => w.uid))
  workforce.forEach(w => {
    w.selected = false
    w.groups = []
  })
  wf_groups.forEach(({ members, name }) => members && members.forEach(email => workersByEmail[email].groups.push(name)) )
  const workforceGroupsById = makeMapFromListOfObjectsAndKey(wf_groups, 'name')
  return { workforce, workersByEmail, workerEmailList, workersByUId, unloadedUserProfileUIds, workforceGroupsById }
}

const createNewWorker = ():WFWorker=>{
  return { firstName:'', lastName:'', email:'', uid:null}
}

export const createNewWorkerPermissions = ():PermissionAccess=>{
  return { types:[] }
}

// Reducers
export const workforceReducer = createReducer(
  makeInitialState(),
  on(WorkforceActions.loadDownloadedWorkerProfiles, (state, { payload }) => {
      if (!payload.length) {
        return {
          ...state,
          loadingProfiles: false
        }
      } else {
        const { unloadedUserProfileUIds, workforce } = state
        const workerProfiles = workerProfileAdapter.addMany(payload, state.workerProfiles)
        workforce.filter(w => unloadedUserProfileUIds.has(w.uid)).forEach(worker => {
          const profile = workerProfiles.entities[worker.uid]
          if (profile) {
            unloadedUserProfileUIds.delete(worker.uid)
            profile && Object.assign(worker, profile) // <== mutating in place, maybe not the best solution
          }
        })
        return {
          ...state,
          workerProfiles,
          workforce: [...workforce],
          workersByEmail: makeMapFromListOfObjectsAndKey<WorkerWithProfile>(workforce, 'email'),
          workersByUId: makeMapFromListOfObjectsAndKey<WorkerWithProfile>(workforce, 'uid'),
          loadingProfiles: false,
          unloadedUserProfileUIds,
        }
      }
    }),
  on(WorkforceActions.saveRole, (state, { payload }) => {
      const updatedWorkforce = state.workforce.map(w => {
        const updatedWorker = payload.find(pw => (pw.uid && pw.uid === w.uid) || pw.email === w.email)
        return updatedWorker ? { ...w, ...updatedWorker } : w
      })
      return {
        ...state,
        workforce: updatedWorkforce
      }
    }),
  on(WorkforceActions.changeNewWorkerStatus, (state, { payload }) => {
      return {
        ...state,
        newWorkerStatus: payload
      }
    }),
  on(WorkforceActions.getFreshWorker, (state) => {
      return {
        ...state,
        newWorker:createNewWorker(),
        newWorkerPermissions:createNewWorkerPermissions()
      }
    }),
  on(WorkforceActions.updateNewWorker, (state, { payload }) => {
      return {
        ...state,
        newWorker:{...state.newWorker, ...payload}
      }
    }),
  on(WorkforceActions.updateNewWorkerPermissions, (state, { payload }) => {
      return {
        ...state,
        newWorkerPermissions:{...state.newWorkerPermissions, ...payload}
      }
    }),
  on(WorkforceActions.loadNewWorkerAndPermissions, (state, { payload }) => {
      const { newWorker, newWorkerPermissions } = payload
      // console.log({ newWorker, newWorkerPermissions })
      return { ...state, newWorker, newWorkerPermissions }
    }),
  on(WorkforceActions.loadDownloadedWorkerProfiles, (state, { payload }) => {
      if (!payload.length) {
        return {
          ...state,
          loadingProfiles: false
        }
      } else {
        const { unloadedUserProfileUIds, workforce } = state
        const workerProfiles = workerProfileAdapter.addMany(payload, state.workerProfiles)
        workforce.filter(w => unloadedUserProfileUIds.has(w.uid)).forEach(worker => {
          const profile = workerProfiles.entities[worker.uid]
          if (profile) {
            unloadedUserProfileUIds.delete(worker.uid)
            profile && Object.assign(worker, profile) // <== mutating in place, maybe not the best solution
          }
        })
        return {
          ...state,
          workerProfiles,
          workforce: [...workforce],
          workersByEmail: makeMapFromListOfObjectsAndKey<WorkerWithProfile>(workforce, 'email'),
          workersByUId: makeMapFromListOfObjectsAndKey<WorkerWithProfile>(workforce, 'uid'),
          loadingProfiles: false,
          unloadedUserProfileUIds,
        }
      }
    }),
  on(permissionsActions.addEventWidePermissionsForSingleUser, (state, action) => {
      const workforce = state.workforce.map(w => ({ ...w }))
      const worker = workforce.find(w => w.uid === action.uid_or_email || w.email === action.uid_or_email)
      worker.permissions = action.types.length ? action.types.map(capitalize).map(p => p === "Crew" ? "Guest" : p).join(", ") : ""
      return { ...state, workforce }
    }),
  on(permissionsActions.loadEventWidePermissions, (state, action) => {
      if(!action.payload) return state;
      const workforce = state.workforce.map(w => ({ ...w }))
      const updated_workforce = []
      workforce.forEach(worker=>{
        const permissions = worker.uid && action.payload[worker.uid] ? action.payload[worker.uid] : action.payload[worker.email]
        const updated_worker = {...worker};
        if (permissions) updated_worker.permissions = permissions.length ? permissions.map(capitalize).map(p => p === "Crew" ? "Guest" : p).join(", ") : ""
        updated_workforce.push(updated_worker)
      })
      return { ...state, workforce:updated_workforce}
    }),
  on(eventsActions.addManyEvents, (state, action) => {
      const stateCopy = jsonCopyObject(state);
      const { allWorkforceGroupsById, allWorkersByUId, allWorkersByEmail } = stateCopy
      action.payload.forEach(e => {
        if (e.wf_groups?.length) e.wf_groups.forEach(group => allWorkforceGroupsById[group.name] = group)
        if (!allWorkersByUId[e.eventId]) allWorkersByUId[e.eventId] = {}
        if (!allWorkersByEmail[e.eventId]) allWorkersByEmail[e.eventId] = {}
        if (!e.workforce) return
        e.workforce.forEach(w => {
          allWorkersByEmail[e.eventId][w.email] = w
          if (w.uid) allWorkersByUId[e.eventId][w.uid] = w
        })
      })
      return {
        ...state,
        allWorkersByEmail,
        allWorkersByUId,
        allWorkforceGroupsById
      }
    }),
  on(eventsActions.loadEvent, (state, action) => {
    const payload = jsonCopyObject(action.payload);
      const { wf_groups, workforce, invitation } = payload
      const { workforce: updatedWorkforce, workersByEmail, workerEmailList, workersByUId, unloadedUserProfileUIds, workforceGroupsById } = extractWorkforceObjectsAndInitialize(workforce, state.workerProfiles.entities, wf_groups)
      return {
        ...state,
        wf_groups,
        workforce: updatedWorkforce,
        declined_workers: invitation ? invitation.filter(inv => inv.status === 'declined') : [],
        unloadedUserProfileUIds,
        loadingProfiles: !!unloadedUserProfileUIds.size,
        workersByEmail,
        workerEmailList,
        workersByUId,
        workforceGroupsById
      }
    }),
  on(eventsActions.unloadEvent, (state) => {
      return {
        ...makeInitialState(),
        workerProfiles: state.workerProfiles // keep worker profiles as they remain the same across events
      }
    }),
  on(userActions.logout, (state) => {
      return makeInitialState()
    }),
  on(userActions.updateUser, (state, action) => {
      const { id, changes } = action
      const stateCopy = jsonCopyObject(state);
      const { workersByUId, workersByEmail, workforce, workerProfiles } = stateCopy
      const index = workforce.findIndex(w => w.uid === id)
      if (index < 0 ) {
        console.log("User not found in workforce (not loaded yet?)")
      }
      const updatedWorker: WFWorker = (index < 0 ? { ...changes } : { ...workforce[index], ...changes }) as WFWorker
      workforce[index] = updatedWorker 
      workersByUId[id] = updatedWorker 
      workersByEmail[updatedWorker.email] = updatedWorker
      workerProfiles[id] = updatedWorker
      return { ...state, workforce, workersByUId, workersByEmail, workerProfiles }
    }),
)

export const workforceFeature = createFeature({
  name: 'workforce',
  reducer: workforceReducer,
});

export const workforceStateSelector = createFeatureSelector<WorkforceState>('workforce');


